import { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '@services/authService';
import toast from 'react-hot-toast';

const AuthContext = createContext();

// Auth state reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.access_token || action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    default:
      return state;
  }
};

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('rdx_auth_token');
        const user = localStorage.getItem('rdx_user');

        if (token && user) {
          // Verify token is still valid
          const isValid = await authService.verifyToken(token);

          if (isValid) {
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: JSON.parse(user),
                token
              }
            });
          } else {
            // Token is invalid, clear storage
            localStorage.removeItem('rdx_auth_token');
            localStorage.removeItem('rdx_user');
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (emailOrUsername, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const credentials = {
        username: emailOrUsername,
        password: password
      };

      const response = await authService.login(credentials);

      // Store in localStorage (handle both access_token and token)
      const token = response.access_token || response.token;
      localStorage.setItem('rdx_auth_token', token);
      localStorage.setItem('rdx_user', JSON.stringify(response.user));

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: response
      });

      toast.success(`Welcome back, ${response.user.first_name}!`);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear storage regardless of API call success
      localStorage.removeItem('rdx_auth_token');
      localStorage.removeItem('rdx_user');

      dispatch({ type: 'LOGOUT' });
      toast.success('Logged out successfully');
    }
  };

  // Update user profile
  const updateProfile = async userData => {
    try {
      const updatedUser = await authService.updateProfile(userData);

      // Update localStorage
      localStorage.setItem('rdx_user', JSON.stringify(updatedUser));

      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
      toast.success('Profile updated successfully');

      return updatedUser;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Profile update failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Change password
  const changePassword = async passwordData => {
    try {
      await authService.changePassword(passwordData);
      toast.success('Password changed successfully');
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Password change failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Forgot password
  const forgotPassword = async email => {
    try {
      await authService.forgotPassword(email);
      toast.success('Password reset email sent');
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to send reset email';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (token, newPassword) => {
    try {
      await authService.resetPassword(token, newPassword);
      toast.success('Password reset successfully');
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Password reset failed';
      toast.error(errorMessage);
      throw error;
    }
  };

  // Check if user has permission
  const hasPermission = (permission, moduleId = null, branchId = null) => {
    if (!state.user) return false;

    // Admin has all permissions
    if (state.user.role === 'admin') return true;

    // Check user permissions
    if (state.user.permissions) {
      return state.user.permissions.some(perm => {
        const moduleMatch = !moduleId || perm.module_id === moduleId;
        const branchMatch = !branchId || perm.branch_id === branchId;
        return moduleMatch && branchMatch && perm[permission];
      });
    }

    return false;
  };

  // Check if user has role
  const hasRole = role => {
    return state.user?.role === role || state.user?.role?.toLowerCase() === role?.toLowerCase();
  };

  // Check if user is admin
  const isAdmin = () => {
    return hasRole('admin');
  };

  // Get user's branch info
  const getUserBranch = () => {
    return {
      id: state.user?.branch_id,
      department: state.user?.department
    };
  };

  const value = {
    ...state,
    login,
    logout,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    hasPermission,
    hasRole,
    isAdmin,
    getUserBranch,
    clearError: () => dispatch({ type: 'CLEAR_ERROR' })
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
